/**
 * Groups the Zoe Q&A log's per-question rows into conversations.
 *
 * The log is fetched flat — one entry per question — because that is what
 * filters, search and exports work on. Reading it flat, though, strips every
 * question of its context: a clarification reply ("Orders due on that date…")
 * is meaningless without the question it resolved, and a reviewer cannot
 * judge an answer without the turns before it. So the page reads it grouped.
 *
 * Pure, so the rules — ordering, totals, which figures come from where — are
 * testable without rendering a table.
 */

import type { QuestionLogConversation, QuestionLogEntry, TurnUsage } from '../types/zoeFeedback';

export interface ConversationGroup {
  /** `user_id|conversation_id` — conversation ids are only unique per user. */
  key: string;
  conversation_id: string;
  user_id: string;
  user_label?: string;
  /** The conversation's first question. */
  title: string;
  /** Its questions in this log, in conversation order. */
  turns: QuestionLogEntry[];
  /** When it was last active, from the newest question shown. */
  lastAskedAt: string;
  /** Every question it has had; can exceed `turns.length` when some fall outside the period. */
  turnCount: number;
  /** Billed tokens and wall-clock time — see `billedTokens`. */
  usage?: { tokens: number; durationMs: number; answers: number };
  flagged: number;
  good: number;
  errored: number;
  notes: number;
}

/**
 * Everything the run was billed for. `total_tokens` is prompt + completion with
 * cached tokens metered separately (that is how Bedrock reports them), so the
 * figure a reader would recognise as "the tokens this cost" adds all three.
 */
export function billedTokens(usage: Pick<TurnUsage, 'total_tokens' | 'cache_read_tokens' | 'cache_write_tokens'>): number {
  return usage.total_tokens + usage.cache_read_tokens + usage.cache_write_tokens;
}

/**
 * @param entries - the flat log, any order.
 * @param conversations - the server's per-conversation lines, when it sends
 *   them. They carry the true title and turn count and the conversation's
 *   whole-life cost; without them (an older server, or an errored first
 *   question that never became a conversation) both are derived from the rows.
 */
export function groupQuestionLog(
  entries: QuestionLogEntry[],
  conversations: QuestionLogConversation[] = [],
): ConversationGroup[] {
  const meta = new Map(conversations.map((c) => [`${c.user_id}|${c.conversation_id}`, c]));
  const byKey = new Map<string, QuestionLogEntry[]>();
  for (const e of entries) {
    const key = `${e.user_id}|${e.conversation_id}`;
    const list = byKey.get(key);
    if (list) list.push(e);
    else byKey.set(key, [e]);
  }

  const groups: ConversationGroup[] = [];
  for (const [key, list] of byKey) {
    const turns = [...list].sort(conversationOrder);
    const first = turns[0]!;
    const info = meta.get(key);
    const lastAskedAt = turns.reduce((latest, t) => (t.asked_at > latest ? t.asked_at : latest), first.asked_at);
    groups.push({
      key,
      conversation_id: first.conversation_id,
      user_id: first.user_id,
      ...(info?.user_label ?? first.user_label ? { user_label: info?.user_label ?? first.user_label } : {}),
      title: info?.title || first.question,
      turns,
      lastAskedAt,
      turnCount: Math.max(info?.turn_count ?? 0, turns.length),
      ...usageOf(info, turns),
      flagged: turns.filter((t) => t.feedback?.rating === 'down').length,
      good: turns.filter((t) => t.feedback?.rating === 'up').length,
      errored: turns.filter((t) => t.status === 'error').length,
      notes: turns.reduce((n, t) => n + t.notes.length, 0),
    });
  }
  return groups.sort((a, b) => b.lastAskedAt.localeCompare(a.lastAskedAt));
}

/**
 * The conversation's cost. The server's running total is preferred: it covers
 * the whole conversation, including questions outside the period shown.
 * Otherwise summed from the rows here, which is exact for anything that fits
 * in the period.
 */
function usageOf(
  info: QuestionLogConversation | undefined,
  turns: QuestionLogEntry[],
): { usage?: ConversationGroup['usage'] } {
  if (info?.usage_total) {
    return {
      usage: {
        tokens: billedTokens(info.usage_total),
        durationMs: info.usage_total.duration_ms,
        answers: info.usage_total.turns,
      },
    };
  }
  const withUsage = turns.filter((t) => t.usage);
  if (withUsage.length === 0) return {};
  return {
    usage: {
      tokens: withUsage.reduce((n, t) => n + billedTokens(t.usage!), 0),
      durationMs: withUsage.reduce((n, t) => n + t.usage!.duration_ms, 0),
      answers: withUsage.length,
    },
  };
}

/**
 * Time, then position. Not "position when both rows have one, else time": an
 * errored question never becomes a turn and has no position, and mixing the
 * two keys pair-by-pair is not a consistent ordering, which a sort requires.
 * Positions are assigned in time order, so this agrees with them wherever
 * both exist, and `seq` still settles two questions in the same millisecond.
 */
function conversationOrder(a: QuestionLogEntry, b: QuestionLogEntry): number {
  return a.asked_at.localeCompare(b.asked_at) || (a.seq ?? 0) - (b.seq ?? 0);
}

/** Compact token count: "842", "12.4k", "1.2M". */
export function formatTokens(total: number): string {
  if (total >= 1_000_000) return `${(total / 1_000_000).toFixed(1)}M`;
  if (total >= 10_000) return `${(total / 1000).toFixed(1)}k`;
  return total.toLocaleString('en-GB');
}
